import React, { memo, useEffect, useRef, useState } from 'react';
import { twJoin, twMerge } from 'tailwind-merge';
import { match } from 'ts-pattern';
import { usePrevious } from '../../hooks/usePrevious';
import { formatNumber } from '../../lib/formatters';
import { Gwei } from '../../lib/gwei';
import { OrderbookColumnOrder } from './OrderbookRows';

const HIGHLIGHT_DURATION = 500;

interface OrderbookRowProps {
  type: 'bid' | 'ask';
  reversed?: boolean;
  price: bigint | undefined;
  quantity: bigint | undefined;
  depth: bigint | undefined;
  width: string;
  priceDecimals: number;
  hasOpenOrder: boolean;
  formatValue: (quantity: bigint | undefined, price: bigint | undefined) => string;
  onRowClick: (price: bigint, amount: bigint) => void;
  orderbookMounted: boolean;
  showTotal?: boolean;
  columnOrder?: OrderbookColumnOrder[];
}

/**
 * Individual row of the orderbook, displaying price, size and cumulative size.
 * Styled red or green based on the order type.
 */
export const OrderbookRow: React.FC<OrderbookRowProps> = memo(
  ({
    type,
    reversed = false,
    price,
    quantity,
    depth,
    width,
    priceDecimals,
    hasOpenOrder,
    formatValue,
    onRowClick,
    orderbookMounted,
    showTotal = true,
    columnOrder = ['price', 'size', 'total'] as const,
  }) => {
    const [shouldHighlight, setShouldHighlight] = useState(false);
    const prevQuantity = usePrevious(quantity);
    const timerRef = useRef<NodeJS.Timeout | null>(null);
    useEffect(() => {
      if (quantity === undefined || !orderbookMounted) {
        return;
      }
      if (prevQuantity !== quantity) {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }
        setShouldHighlight(true);

        timerRef.current = setTimeout(() => {
          setShouldHighlight(false);
          timerRef.current = null;
        }, HIGHLIGHT_DURATION);
      }
    }, [orderbookMounted, prevQuantity, quantity]);

    useEffect(() => {
      // Cleanup on unmount only - avoids interrupting highlight on re-renders.
      return () => {
        if (timerRef.current) {
          clearTimeout(timerRef.current);
        }
      };
    }, []);

    return (
      <div
        onClick={() => {
          if (price && quantity) {
            onRowClick(price, quantity);
          }
        }}
        data-testid={`${type}-row-${Gwei.fmt(price || 0n)}`}
        className={twJoin(
          'relative rounded-sm py-1.5 text-2xs transition-colors 4xl:py-1',
          price !== undefined && 'hover:bg-backgroundHoverOverlay',
          shouldHighlight && (type === 'bid' ? 'bg-green-500/10' : 'bg-red-500/10'),
          price !== undefined && 'cursor-pointer'
        )}
      >
        {hasOpenOrder && price !== undefined && (
          <div
            aria-label="Open order in price group"
            className={twJoin(
              'absolute left-0 top-0 h-full w-0.5',
              type === 'bid' ? 'bg-backgroundGreen' : 'bg-backgroundRed'
            )}
          />
        )}
        <div
          className="grid gap-x-3 px-2"
          style={{ gridTemplateColumns: `repeat(${columnOrder.length}, 1fr)` }}
        >
          {(reversed ? [...columnOrder].reverse() : columnOrder).map((column, index) => {
            const isFirst = index === 0;

            return match(column)
              .with('price', () => (
                <div
                  key="price"
                  className={twMerge(
                    type === 'bid' ? 'text-contentGreen' : 'text-contentRed',
                    price === undefined && 'text-contentTertiary',
                    isFirst ? 'text-left' : 'text-right'
                  )}
                >
                  {formatNumber(price, {
                    maximumFractionDigits: priceDecimals,
                    minimumFractionDigits: priceDecimals,
                  })}
                </div>
              ))
              .with('size', () => (
                <div
                  key="size"
                  className={twMerge('text-contentTertiary', isFirst ? 'text-left' : 'text-right')}
                >
                  {formatValue(quantity, price)}
                </div>
              ))
              .with('total', () =>
                showTotal ? (
                  <div
                    key="total"
                    className={twMerge(
                      'text-contentTertiary',
                      isFirst ? 'text-left' : 'text-right'
                    )}
                  >
                    {formatValue(depth, price)}
                  </div>
                ) : null
              )
              .exhaustive();
          })}
        </div>
        {price !== undefined && (
          <div
            className={twJoin(
              'absolute inset-y-0 rounded-sm opacity-20',
              reversed ? 'left-0' : 'right-0',
              type === 'ask' ? 'bg-contentRed' : 'bg-contentGreen'
            )}
            style={{ width }}
          />
        )}
      </div>
    );
  }
);
